import { makeAsync } from '@client/new/helpers/async';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useLoginStore } from '@client/new/stores/login.store';
import { navigate } from '@client/routes';
import { track } from '@services/tracking';
import { useState } from 'react';
import { navigateWithAuthTransition } from '../auth.navigation';
import { useAuthModel } from '../model/auth.model';
import { authPath } from '../model/auth.paths.model';
import { isEmailValid } from './register.progress';

export const LOGIN_EMAIL_PRIVACY_MESSAGE =
  "If the email exists, we'll send a confirmation code to that address.";

type LoginActionMap = {
  setEmail: string;
  continue: null;
  goToSignUp: null;
};

type LoginAction = Actions<LoginActionMap>;

type LoginState = {
  email: string;
  canContinue: boolean;
  submitState: Async<null>;
};

export type LoginViewModel = {
  state: LoginState;
} & Dispatchers<LoginActionMap>;

export const useLoginViewModel = (): Async<LoginViewModel> => {
  const model = useAuthModel();
  const identity = model.queries.identity;
  const { setEmail } = useLoginStore();
  const [email, setLocalEmail] = useState('');
  const [submitState, setSubmitState] = useState<Async<null>>(
    makeAsync.notFetched(),
  );

  const updateEmail = (value: string): void => {
    setLocalEmail(value);
    setSubmitState(makeAsync.notFetched());
  };

  const continueSignIn = async (): Promise<void> => {
    if (!isEmailValid(email, identity)) {
      return;
    }

    setSubmitState(makeAsync.fetching());
    const emailValue = identity.parseEmail(email);
    if (!emailValue) {
      setSubmitState(makeAsync.error(LOGIN_EMAIL_PRIVACY_MESSAGE));
      return;
    }

    try {
      model.dispatch('ClearEmailChallenge', { email: emailValue });
      await model.dispatch('SendSignInChallenge', { email: emailValue });
      model.dispatch('PersistDraftField', {
        key: 'email',
        value: emailValue,
      });
      model.dispatch('PersistFlowSession', {
        step: 'code',
        source: 'signin',
        email: emailValue,
      });
      setEmail(emailValue);
      navigate(authPath('/login/confirmation-code'));
      setSubmitState(makeAsync.fetched(null));
    } catch {
      setSubmitState(makeAsync.error(LOGIN_EMAIL_PRIVACY_MESSAGE));
    }
  };

  const goToSignUp = (): void => {
    track({ type: 'Sign Up', payload: { uid: '' } });
    model.dispatch('ClearFlowSession');
    navigateWithAuthTransition(authPath('/signup'));
  };

  const _dispatch = (action: LoginAction): void => {
    switch (action.type) {
      case 'setEmail':
        updateEmail(action.payload);
        return;
      case 'continue':
        void continueSignIn();
        return;
      case 'goToSignUp':
        goToSignUp();
        return;
    }
  };

  return toAsyncFetched({
    state: {
      email,
      canContinue: isEmailValid(email, identity),
      submitState,
    },
    ...getDispatchers<LoginActionMap>(_dispatch),
  });
};
